import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import Gallery from "../components/Gallery";
import StatusBadge from "../components/StatusBadge";
import { PROMISES } from "../content/site";
import { carPath, carStatus, carTitle, conditionLabel, formatKm, formatPrice, priceWithoutVat } from "../lib/format";
import useDocumentTitle from "../lib/useDocumentTitle";
import NotFound from "./NotFound";
import s from "./CarDetail.module.css";

export default function CarDetail({ cars, carsStatus }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const id = parseInt(slug, 10);
  const car = cars.find((c) => c.id === id);
  const [imgIdx, setImgIdx] = useState(0);
  const [tab, setTab] = useState("parametry");
  const stageRef = useRef(null);
  useDocumentTitle(car ? `${carTitle(car)} ${car.year}` : "Vůz");

  // Moving between cars via the bottom bar reuses this component.
  useEffect(() => { setImgIdx(0); setTab("parametry"); }, [id]);

  if (carsStatus === "loading") return <div className="container" style={{ minHeight: "60vh" }} aria-busy="true" />;
  if (!car) return <NotFound title="Vůz jsme nenašli" text="Mohl být mezitím prodán nebo stažen z nabídky." />;

  const status = carStatus(car);
  const title = carTitle(car);
  const images = car.images || [];

  // Prev / next among cars still on offer, in the inventory's default order.
  const onOffer = cars.filter((c) => carStatus(c) !== "sold" || c.id === car.id)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const pos = onOffer.findIndex((c) => c.id === car.id);
  const prevCar = onOffer.length > 1 ? onOffer[(pos - 1 + onOffer.length) % onOffer.length] : null;
  const nextCar = onOffer.length > 1 ? onOffer[(pos + 1) % onOffer.length] : null;

  const keyFacts = [
    ["Nájezd", formatKm(car.mileage)],
    ["Dojezd", car.range ? formatKm(car.range) : null],
    ["Baterie", car.battery],
  ].filter(([, v]) => v);

  const specs = [
    ["Rok výroby", car.year],
    ["Nájezd", formatKm(car.mileage)],
    ["Kapacita baterie", car.battery],
    ["Zdraví baterie (SOH)", car.soh],
    ["Dojezd", car.range ? formatKm(car.range) : null],
    ["Výkon", car.power],
    ["Počet míst", car.seats],
    ["Barva", car.color],
    ["Stav", conditionLabel(car.condition)],
  ].filter(([, v]) => v);

  const tabs = [
    ["parametry", "Parametry"],
    ...(images.length > 1 ? [["galerie", `Galerie (${images.length})`]] : []),
    ...(car.description ? [["popis", "Popis"]] : []),
    ["v-cene", "Co je v ceně"],
  ];

  const onTabKey = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const i = tabs.findIndex(([k]) => k === tab);
    const next = tabs[(i + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length][0];
    setTab(next);
    document.getElementById(`tab-${next}`)?.focus();
  };

  const inquire = () =>
    navigate("/kontakt", { state: { message: `Dobrý den, mám zájem o ${title} (${car.year}, ${car.color}). Je vůz stále k dispozici?` } });

  return (
    <div className={s.page}>
      <div className="container">
        <nav className={s.crumbs} aria-label="Drobečková navigace">
          <Link to="/nabidka"><Icon name="arrow-left" size={16} /> Skladové vozy</Link>
        </nav>

        <div className={s.hero}>
          <div className={s.stage} ref={stageRef}>
            <Gallery images={images} idx={imgIdx} onIdx={setImgIdx} alt={title} dimmed={status === "sold"} />
            <span className={s.shade} aria-hidden="true" />
            <span className={s.year} aria-hidden="true">{car.year}</span>
          </div>

          <aside className={s.panel} aria-label="Cena a hlavní údaje">
            <div className={s.titleRow}>
              <p className="eyebrow">{car.year} · {car.color}</p>
              <StatusBadge status={status} />
            </div>
            <h1 className={s.title}>
              <span className={s.brand}>{car.brand}</span>
              <span className={s.model}>{car.model}</span>
            </h1>

            <p className={s.priceLabel}>Cena vč. DPH</p>
            <p className={`${s.price} num ${status === "sold" ? s.struck : ""}`}>{formatPrice(car.price)}</p>
            <p className={s.vat}>bez DPH <span className="num">{formatPrice(priceWithoutVat(car.price))}</span></p>

            {car.soh && (
              <div className={s.soh}>
                <Icon name="battery" size={22} />
                <p><span className={s.sohValue}>{car.soh}</span> zdraví baterie (SOH), ověřeno diagnostikou</p>
              </div>
            )}

            <dl className={s.facts}>
              {keyFacts.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
            </dl>

            {status === "available" && (
              <button type="button" className="btn btn-primary btn-lg btn-block" onClick={inquire}>
                Zeptat se na tento vůz <Icon name="arrow-right" size={18} />
              </button>
            )}
            {status === "reserved" && (
              <div className={s.notice}>
                <strong>Vůz je rezervovaný.</strong> Pokud rezervace propadne, rádi vám dáme vědět.
                <button type="button" className="btn btn-ghost btn-block" onClick={inquire} style={{ marginTop: 12 }}>Chci být informován</button>
              </div>
            )}
            {status === "sold" && (
              <div className={s.notice}>
                <strong>Tento vůz je prodaný.</strong> Podobný vám ale najdeme a dovezeme na míru.
              </div>
            )}
            <Link to="/dovoz" className="btn btn-ghost btn-block" style={{ marginTop: 10 }}>Raději dovoz na míru</Link>
          </aside>
        </div>

        <div className={s.tabs} role="tablist" aria-label="Informace o voze" onKeyDown={onTabKey}>
          {tabs.map(([k, label]) => (
            <button key={k} id={`tab-${k}`} type="button" role="tab"
              aria-selected={tab === k} aria-controls={`panel-${k}`} tabIndex={tab === k ? 0 : -1}
              className={s.tab} onClick={() => setTab(k)}>
              <Icon name={tab === k ? "chevron-down" : "chevron-right"} size={16} />{label}
            </button>
          ))}
        </div>

        <div id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`} className={s.tabPanel}>
          {tab === "parametry" && (
            <dl className={s.specs}>
              {specs.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
            </dl>
          )}
          {tab === "galerie" && (
            <div className={s.thumbs}>
              {images.map((src, i) => (
                <button key={src + i} type="button" className={`${s.thumb} ${i === imgIdx ? s.thumbActive : ""}`}
                  aria-label={`Zobrazit fotografii ${i + 1}`} aria-current={i === imgIdx}
                  onClick={() => { setImgIdx(i); stageRef.current?.scrollIntoView({ block: "center" }); }}>
                  <img src={src} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          {tab === "popis" && <p className={s.description}>{car.description}</p>}
          {tab === "v-cene" && (
            <ul className={s.included}>
              {PROMISES.map((p) => <li key={p.text}><Icon name={p.icon} size={20} />{p.text}</li>)}
              <li><Icon name="sparkles" size={20} />Kompletní čištění a dezinfekce před předáním</li>
              <li><Icon name="steering-wheel" size={20} />Testovací jízda bez závazků</li>
            </ul>
          )}
        </div>
      </div>

      {prevCar && (
        <nav className={s.carNav} aria-label="Další vozy v nabídce">
          <div className={`container ${s.carNavInner}`}>
            <Link to={carPath(prevCar)} className={s.carLink}>
              <Icon name="chevron-left" size={22} />
              <span><span className={s.carLinkLabel}>Předchozí vůz</span>{carTitle(prevCar)}</span>
            </Link>
            <span className={`${s.carPos} num`}>{pos + 1} / {onOffer.length}</span>
            <Link to={carPath(nextCar)} className={`${s.carLink} ${s.carLinkNext}`}>
              <span><span className={s.carLinkLabel}>Další vůz</span>{carTitle(nextCar)}</span>
              <Icon name="chevron-right" size={22} />
            </Link>
          </div>
        </nav>
      )}
    </div>
  );
}
